/*
 * Credentials page model (decision of 2026-10-22, P13): Apple signing material per iOS app and the upload keystore per
 * Android app, custodied by Malible. Pure functions only: which apps can hold material, the slots of an app, the health of
 * a credential, the facts shown, and who may read or manage. The API stays the only authority (it answers 403/hides what a
 * caller may not see).
 */
import type { App, CustodiedKind, Me } from '@hangar/shared';
import type { Tone } from '@/components/badges';
import type { SigningCredential } from '@/api/endpointsSigning';

export interface SigningSlot {
  kind: CustodiedKind;
  label: string;
  icon: string;
  /** What the file input accepts. */
  accept: string;
  fileLabel: string;
  needsPassword: boolean;
  needsKeyIds: boolean;
  /** An Android keystore: store password, key alias, key password and the optional application id. */
  needsKeystore?: boolean;
  hint: string;
}

export const SIGNING_SLOTS: readonly SigningSlot[] = [
  { kind: 'ios_distribution_certificate', label: 'Distribution certificate', icon: 'workspace_premium', accept: '.p12,application/x-pkcs12', fileLabel: '.p12 file', needsPassword: true, needsKeyIds: false, hint: 'Exported with its private key from the Mac that created it (Keychain Access → Export).' },
  { kind: 'ios_provisioning_profile', label: 'Provisioning profile', icon: 'verified_user', accept: '.mobileprovision', fileLabel: '.mobileprovision file', needsPassword: false, needsKeyIds: false, hint: 'An App Store (or ad hoc) profile for the app\'s bundle id, generated with the certificate above.' },
  { kind: 'asc_api_key', label: 'App Store Connect API key', icon: 'key', accept: '.p8', fileLabel: '.p8 file', needsPassword: false, needsKeyIds: true, hint: 'From App Store Connect → Users and Access → Integrations, with its key id and issuer id.' },
];

/** The Android app's one slot: the upload keystore every build of the app is signed with. */
export const ANDROID_SLOTS: readonly SigningSlot[] = [
  { kind: 'android_upload_keystore', label: 'Signing keystore', icon: 'key', accept: '.jks,.keystore,.p12,application/x-pkcs12', fileLabel: 'keystore file (.jks, .keystore or .p12)', needsPassword: false, needsKeyIds: false, needsKeystore: true, hint: 'The keystore the app is signed with (the Google Play upload key), with its passwords and the alias of the key.' },
];

const IOS_FRAMEWORKS = new Set(['ios_native', 'expo', 'react_native', 'flutter']);
const ANDROID_FRAMEWORKS = new Set(['android_native', 'expo', 'react_native', 'flutter']);

/** Apps that build for iOS, by name. */
export const iosApps = (apps: App[]): App[] => apps.filter((a) => IOS_FRAMEWORKS.has(a.framework)).sort((a, b) => a.name.localeCompare(b.name));

/** Apps that build for Android, by name. */
export const androidApps = (apps: App[]): App[] => apps.filter((a) => ANDROID_FRAMEWORKS.has(a.framework)).sort((a, b) => a.name.localeCompare(b.name));

/** The app's credential for each slot (the newest active one), or null. */
export function slotsOf(appId: string, credentials: SigningCredential[], slots: readonly SigningSlot[] = SIGNING_SLOTS): { slot: SigningSlot; credential: SigningCredential | null }[] {
  return slots.map((slot) => ({
    slot,
    credential: credentials.filter((c) => c.appId === appId && c.kind === slot.kind && c.status !== 'revoked').sort((a, b) => b.id.localeCompare(a.id))[0] ?? null,
  }));
}

const DAY_MS = 86_400_000;

export interface Health {
  tone: Tone;
  label: string;
  detail: string;
}

/** Days until `expiresAt` (negative once expired); null without an expiry. */
export function daysLeft(expiresAt: string | null | undefined, now: number): number | null {
  if (!expiresAt) return null;
  const t = Date.parse(expiresAt);
  return Number.isNaN(t) ? null : Math.ceil((t - now) / DAY_MS);
}

const date = (iso: string): string => new Date(iso).toISOString().slice(0, 10);

/** Health from expiry first (it is what breaks builds), then the stored status. */
export function healthOf(c: SigningCredential | null, now: number): Health {
  if (!c) return { tone: 'subtle', label: 'Missing', detail: 'Not uploaded yet' };
  const left = daysLeft(c.expiresAt, now);
  if (left !== null && left <= 0) return { tone: 'failed', label: 'Expired', detail: `Expired on ${date(c.expiresAt!)}` };
  if (left !== null && left <= 30) return { tone: 'awaiting', label: 'Expiring', detail: `Expires in ${left} day${left === 1 ? '' : 's'} (${date(c.expiresAt!)})` };
  if (c.status === 'mismatch') return { tone: 'failed', label: 'Mismatch', detail: 'Does not match the rest of the app\'s material' };
  if (left !== null) return { tone: 'succeeded', label: 'Valid', detail: `Expires on ${date(c.expiresAt!)}` };
  return { tone: 'succeeded', label: 'Stored', detail: 'Does not expire (revoke it in App Store Connect)' };
}

/** The non-secret facts of a credential, as label/value rows (never the material). */
export function factsOf(c: SigningCredential): { label: string; value: string; mono?: boolean }[] {
  const m = c.metadata;
  const s = (k: string): string | null => (typeof m[k] === 'string' && m[k] ? (m[k] as string) : null);
  if (c.kind === 'android_upload_keystore') return keystoreFacts(c);
  const rows: { label: string; value: string | null; mono?: boolean }[] =
    c.kind === 'ios_distribution_certificate'
      ? [{ label: 'Name', value: s('commonName') }, { label: 'Team', value: [s('teamName'), s('teamId')].filter(Boolean).join(' · ') || null }, { label: 'Serial', value: s('serialNumber'), mono: true }]
      : c.kind === 'ios_provisioning_profile'
        ? [{ label: 'Profile', value: s('name') }, { label: 'Bundle id', value: s('bundleId'), mono: true }, { label: 'Type', value: s('profileType') }, { label: 'UUID', value: s('uuid'), mono: true }]
        : [{ label: 'Key id', value: s('keyId'), mono: true }, { label: 'Issuer id', value: s('issuerId'), mono: true }];
  return rows.filter((r): r is { label: string; value: string; mono?: boolean } => r.value !== null);
}

/** `AA:BB:…`, as `keytool` and the Play Console print a fingerprint. */
export const colonFingerprint = (hex: string): string => hex.toUpperCase().match(/.{1,2}/g)?.join(':') ?? hex.toUpperCase();

/** What an operator needs to recognize a keystore: alias, who it is for, the application id and both fingerprints. */
function keystoreFacts(c: SigningCredential): { label: string; value: string; mono?: boolean }[] {
  const s = (k: string): string | null => (typeof c.metadata[k] === 'string' && c.metadata[k] ? (c.metadata[k] as string) : null);
  const fp = (k: string): string | null => (c.fingerprints?.[k] ? colonFingerprint(c.fingerprints[k]!) : null);
  const rows = [
    { label: 'Key alias', value: s('keyAlias'), mono: true }, { label: 'Subject', value: s('subject') }, { label: 'Application id', value: s('applicationId'), mono: true },
    { label: 'Key', value: [s('keyAlgorithm'), s('format')?.toUpperCase()].filter(Boolean).join(' · ') || null }, { label: 'SHA-1', value: fp('sha1'), mono: true }, { label: 'SHA-256', value: fp('sha256'), mono: true },
  ];
  return rows.filter((r): r is { label: string; value: string; mono?: boolean } => r.value !== null);
}

/** Warnings the API stored with the credential at upload (expiring soon, wildcard profile, …). */
export const warningsOf = (c: SigningCredential): string[] => (Array.isArray(c.metadata.warnings) ? c.metadata.warnings.filter((w): w is string => typeof w === 'string') : []);

export interface SigningAccess {
  canRead: boolean;
  canManage: boolean;
}

/**
 * Reading: org admins and owners (scope `admin`), the project's maintainers, and platform operators. Managing (upload,
 * rotate, revoke): platform operators who can write credentials in this org. `me` undefined (loading) reads, manages nothing.
 */
export function signingAccessOf(me: Me | undefined, projectId: string | undefined): SigningAccess {
  if (!me) return { canRead: true, canManage: false };
  const operator = me.user?.platformRole === 'operator' && me.user.isExternal === false;
  const admin = me.scopes.includes('admin');
  const maintainer = Boolean(projectId) && me.projects.some((p) => p.projectId === projectId && p.role === 'maintainer' && (!p.expiresAt || Date.parse(p.expiresAt) > Date.now()));
  return { canRead: admin || maintainer || operator, canManage: operator && (admin || me.scopes.includes('credentials:write')) };
}

/** What the upload form must have before it can be sent; null when it is complete. */
export function uploadProblem(slot: SigningSlot, v: { file: File | null; password: string; keyId: string; issuerId: string; keyAlias?: string; applicationId?: string }): string | null {
  if (!v.file) return `Choose the ${slot.fileLabel}`;
  if (slot.needsKeystore && !v.password) return 'The keystore password is required';
  if (slot.needsKeystore && !v.keyAlias?.trim()) return 'The key alias is required';
  if (slot.needsKeystore && v.applicationId?.trim() && !/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/.test(v.applicationId.trim())) return 'The application id is the Android package name, such as com.client.app';
  if (slot.needsKeyIds && !/^[A-Z0-9]{10}$/.test(v.keyId.trim())) return 'The key id is 10 letters and digits';
  if (slot.needsKeyIds && !/^[0-9a-f-]{36}$/i.test(v.issuerId.trim())) return 'The issuer id is a UUID';
  return null;
}
